import { UserGroupEventType, UserGroupEventTypeLabels } from '@navio/nsdk';
import { keepPreviousData } from '@tanstack/react-query';
import { noddiAsync, URLKeys } from 'noddi-async';
import { cn, NoddiIcon, type NoddiIconName } from 'noddi-ui-common';
import { ApiErrorMessageInternal, NoddiCard, NoddiSpinner, usePaginationQueryParam } from 'noddi-ui-web';
import { NoddiDateFormats, noddiFormatDate } from 'noddi-util';

type Props = {
  userGroupId: number;
};

const EVENT_META: Record<UserGroupEventType, { icon: NoddiIconName; phrase: string }> = {
  [UserGroupEventType.MEMBER_ADDED]: { icon: 'UserPlus', phrase: 'added a member' },
  [UserGroupEventType.MEMBER_REMOVED]: { icon: 'UserMinus', phrase: 'removed a member' },
  [UserGroupEventType.INVITE_SENT]: { icon: 'Mail', phrase: 'sent an invite' },
  [UserGroupEventType.INVITE_ACCEPTED]: { icon: 'CircleCheck', phrase: 'accepted an invite' },
  [UserGroupEventType.CAR_MANAGER_ASSIGNED]: { icon: 'Car', phrase: 'assigned a car manager' },
  [UserGroupEventType.CAR_MANAGER_CHANGED]: { icon: 'Car', phrase: 'changed the car manager' },
  [UserGroupEventType.ADMIN_CHANGED]: { icon: 'Shield', phrase: 'changed admin access' },
  [UserGroupEventType.PRIMARY_CONTACT_CHANGED]: { icon: 'Star', phrase: 'changed the primary contact' },
  [UserGroupEventType.ADDRESS_ADDED]: { icon: 'Map', phrase: 'added an address' },
  [UserGroupEventType.ADDRESS_REMOVED]: { icon: 'Map', phrase: 'removed an address' },
  [UserGroupEventType.ADDRESS_UPDATED]: { icon: 'Map', phrase: 'updated an address' },
  [UserGroupEventType.EMAIL_CHANGED]: { icon: 'Mail', phrase: 'changed the email' },
  [UserGroupEventType.NAME_CHANGED]: { icon: 'User', phrase: 'changed the name' },
  [UserGroupEventType.PHONE_CHANGED]: { icon: 'Phone', phrase: 'changed the phone number' }
};

export const UserGroupActivityTab = ({ userGroupId }: Props) => {
  const { pagination } = usePaginationQueryParam({ paramKeyPrefix: 'events' });

  const { data, isPending, error } = noddiAsync.useGet({
    type: URLKeys.getPaginatedUserGroupEvents,
    input: { userGroupId, ...pagination },
    queryConfig: { placeholderData: keepPreviousData }
  });

  if (error) {
    return <ApiErrorMessageInternal error={error} />;
  }

  if (isPending) {
    return (
      <div className='flex items-center justify-center py-10'>
        <NoddiSpinner />
      </div>
    );
  }

  const events = data?.results ?? [];

  return (
    <NoddiCard title='Events' icon='Activity' iconTile size='small'>
      {events.length === 0 ? (
        <p className='text-sm text-muted-foreground'>No events yet.</p>
      ) : (
        <div className='flex flex-col gap-4'>
          {events.map((event) => {
            const actorName = event.actorName ?? 'System';
            const timestamp = noddiFormatDate(event.createdAt, NoddiDateFormats.DATE_TIME);
            const eventType = event.eventType;
            const meta = EVENT_META[eventType];
            const icon = meta?.icon ?? 'Circle';
            const phrase =
              meta?.phrase ?? UserGroupEventTypeLabels[eventType] ?? eventType.toLowerCase().replaceAll('_', ' ');
            const detail = event.detail?.trim() || null;
            const subjectLine = buildSubjectLine(event);

            return (
              <div key={event.id} className='flex items-start gap-3'>
                <div className={cn('mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-muted')}>
                  <NoddiIcon name={icon} size='small' className='text-muted-foreground' />
                </div>
                <div className='flex min-w-0 flex-1 flex-col gap-0.5'>
                  <div className='flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-sm'>
                    <span className='font-medium'>{actorName}</span>
                    <span className='text-muted-foreground'>{phrase}</span>
                    <span className='ml-auto text-xs text-muted-foreground'>{timestamp}</span>
                  </div>
                  {subjectLine ? <p className='text-xs text-muted-foreground'>{subjectLine}</p> : null}
                  {detail ? <p className='text-xs whitespace-pre-wrap text-muted-foreground'>{detail}</p> : null}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </NoddiCard>
  );
};

function buildSubjectLine(event: {
  subjectUserName?: string | null;
  carManagerFromUserName?: string | null;
  carManagerToUserName?: string | null;
}): string | null {
  if (event.carManagerFromUserName || event.carManagerToUserName) {
    const fromName = event.carManagerFromUserName ?? 'none';
    const toName = event.carManagerToUserName ?? 'none';
    return `Car manager: ${fromName} -> ${toName}`;
  }
  if (event.subjectUserName) {
    return event.subjectUserName;
  }
  return null;
}
